import {
  Box, Flex, HStack, Button,
  Table, Thead, Tbody, Tr, Th, Td,
  Text, Badge, Switch, Spinner, IconButton, Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, ViewIcon } from "@chakra-ui/icons";
import { hasRole } from "../../../lib/auth";
import { useProcessHubForms } from "./useProcessHubForms";
import { FormTypeModal } from "./FormTypeModal";
import { FormFieldsDrawer } from "./FormFieldsDrawer";
import { LeaderFormsGrid } from "../leader/LeaderFormsGrid";
import { ErrorState } from "../../../components/ErrorState";
import { ProcessHubFormsProvider } from "./ProcessHubFormsContext";

export default function ProcessHubFormsPage() {
  return (
    <ProcessHubFormsProvider>
      <ProcessHubFormsContent />
    </ProcessHubFormsProvider>
  );
}

function ProcessHubFormsContent() {
  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const subtleColor = useColorModeValue("#84858c", "#718096");
  const thColor     = useColorModeValue("#84858c", "#718096");
  const cardBg      = useColorModeValue("white", "#0f2040");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const theadBg     = useColorModeValue("rgba(0,158,251,0.04)", "rgba(0,158,251,0.06)");
  const rowHoverBg  = useColorModeValue("rgba(0,158,251,0.025)", "rgba(0,158,251,0.04)");

  const isRhAdmin = hasRole("rh") || hasRole("admin");

  const {
    forms,
    loading,
    error,
    isFormModalOpen,
    setIsFormModalOpen,
    isFieldsDrawerOpen,
    setIsFieldsDrawerOpen,
    selectedForm,
    handleCreateOpen,
    handleEditOpen,
    handleManageFields,
    handleToggleActive,
    refreshList,
  } = useProcessHubForms();

  if (!isRhAdmin) {
    return (
      <Box p={{ base: 4, md: 8 }}>
        <Text fontSize="2xl" fontWeight={700} color={titleColor} mb={1}>
          Formulários
        </Text>
        <Text fontSize="sm" color={descColor} mb={6}>
          Abra uma nova solicitação para o RH a partir dos formulários padrão da empresa.
        </Text>
        <LeaderFormsGrid />
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="flex-start" mb={6} gap={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight={700} color={titleColor} mb={1}>
            Formulários
          </Text>
          <Text fontSize="sm" color={descColor}>
            Configure os tipos de formulário e seus campos para o Process Hub.
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={AddIcon} />}
          bg="#009efb"
          color="white"
          _hover={{ bg: "#007bd4" }}
          borderRadius="50px"
          h="40px"
          px={5}
          fontSize="14px"
          fontWeight={500}
          onClick={handleCreateOpen}
          flexShrink={0}
        >
          Novo formulário
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={16}>
          <Spinner size="md" color="#009efb" />
        </Flex>
      ) : error ? (
        <ErrorState onRetry={refreshList} />
      ) : forms.length === 0 ? (
        <Text color={subtleColor} fontSize="sm">Nenhum formulário cadastrado.</Text>
      ) : (
        <Box
          bg={cardBg}
          borderRadius="12px"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="0px 4px 8px 0px rgba(14, 61, 93, 0.08)"
          overflow="hidden"
          overflowX="auto"
        >
          <Table variant="simple" minW="520px">
            <Thead bg={theadBg}>
              <Tr>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Nome</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Descrição</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Status</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" textTransform="none" py={3}>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {forms.map((form) => (
                <Tr key={form.id} _hover={{ bg: rowHoverBg }} transition="background 0.12s">
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontWeight={600} fontSize="sm" color={titleColor}>{form.name}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontSize="sm" color={descColor}>{form.description || "—"}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <HStack spacing={2}>
                      <Switch
                        colorScheme="blue"
                        isChecked={form.is_active}
                        onChange={() => handleToggleActive(form)}
                        size="sm"
                      />
                      <Badge
                        borderRadius="50px"
                        fontSize="11px"
                        fontWeight={600}
                        px={2.5}
                        py={0.5}
                        bg={form.is_active ? "rgba(0,164,110,0.12)" : "rgba(220,53,69,0.10)"}
                        color={form.is_active ? "#00a46e" : "#dc3545"}
                      >
                        {form.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </HStack>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <HStack spacing={0}>
                      <IconButton
                        aria-label="Editar formulário"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        color="#009efb"
                        _hover={{ bg: "rgba(0,158,251,0.08)" }}
                        onClick={() => handleEditOpen(form)}
                      />
                      <IconButton
                        aria-label="Gerenciar campos"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="ghost"
                        color={subtleColor}
                        _hover={{ bg: "rgba(0,158,251,0.08)", color: "#009efb" }}
                        onClick={() => handleManageFields(form)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <FormTypeModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        formType={selectedForm}
        onSuccess={refreshList}
      />

      <FormFieldsDrawer
        isOpen={isFieldsDrawerOpen}
        onClose={() => setIsFieldsDrawerOpen(false)}
        formType={selectedForm}
      />

      <Box mt={10} pt={8} borderTopWidth="1px" borderColor={cardBorder}>
        <Text fontSize="xl" fontWeight={700} color={titleColor} mb={1}>
          Preencher uma solicitação
        </Text>
        <Text fontSize="sm" color={descColor} mb={6}>
          Abra uma solicitação a partir dos formulários ativos — inclusive se você, como RH, também for líder de um time.
        </Text>
        <LeaderFormsGrid />
      </Box>
    </Box>
  );
}
